import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Clock, CheckSquare, Loader2, User, Calendar, X, Ticket, Coins } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/payments")({
  head: () => ({ meta: [{ title: "Ödemeler — JUËN Admin" }] }),
  component: AdminPaymentsPage,
});

interface OrderRow {
  id: string;
  user_id: string;
  event_id: string;
  ticket_type_id: string | null;
  quantity: number;
  total_kurus: number;
  status: string;
  payment_method: string;
  admin_notes: string | null;
  created_at: string;
  paid_at: string | null;
  profiles?: { first_name: string; last_name: string; email: string } | null;
  events?: { name: string; starts_at: string } | null;
  ticket_types?: { name: string } | null;
}

interface PackageOrderRow {
  id: string;
  user_id: string;
  package_id: string;
  points: number;
  price_kurus: number;
  status: string;
  admin_notes: string | null;
  created_at: string;
  marked_paid_at: string | null;
  shopier_order_id: string | null;
  shopier_buyer_email: string | null;
  shopier_matched_at: string | null;
  profiles?: { first_name: string; last_name: string; email: string } | null;
}

type Filter = "pending" | "paid";
type Scope = "tickets" | "packages";

function AdminPaymentsPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [packageOrders, setPackageOrders] = useState<PackageOrderRow[]>([]);
  const [scope, setScope] = useState<Scope>("tickets");
  const [filter, setFilter] = useState<Filter>("pending");
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  const loadTickets = useCallback(async () => {
    const { data: simple, error } = await supabase
      .from("orders")
      .select("*")
      .eq("status", filter)
      .order("created_at", { ascending: false })
      .limit(200);

    if (error) {
      toast.error(error.message);
      setOrders([]);
      return;
    }

    const rows = (simple ?? []) as OrderRow[];
    const userIds = [...new Set(rows.map((r) => r.user_id).filter(Boolean))];
    const eventIds = [...new Set(rows.map((r) => r.event_id).filter(Boolean) as string[])];
    const ttIds = [...new Set(rows.map((r) => r.ticket_type_id).filter(Boolean) as string[])];

    const [{ data: profs }, { data: evs }, { data: tts }] = await Promise.all([
      userIds.length
        ? supabase.from("profiles").select("id,first_name,last_name,email").in("id", userIds)
        : Promise.resolve({ data: [] as { id: string; first_name: string; last_name: string; email: string }[] }),
      eventIds.length
        ? supabase.from("events").select("id,name,starts_at").in("id", eventIds)
        : Promise.resolve({ data: [] as { id: string; name: string; starts_at: string }[] }),
      ttIds.length
        ? supabase.from("ticket_types").select("id,name").in("id", ttIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[] }),
    ]);

    const profMap = new Map((profs ?? []).map((p) => [p.id, p]));
    const evMap = new Map((evs ?? []).map((e) => [e.id, e]));
    const ttMap = new Map((tts ?? []).map((t) => [t.id, t]));

    setOrders(
      rows.map((r) => ({
        ...r,
        profiles: profMap.get(r.user_id) ?? null,
        events: r.event_id ? evMap.get(r.event_id) ?? null : null,
        ticket_types: r.ticket_type_id ? ttMap.get(r.ticket_type_id) ?? null : null,
      })),
    );
  }, [filter]);

  const loadPackages = useCallback(async () => {
    const { data, error } = await supabase
      .from("point_package_orders")
      .select("*")
      .eq("status", filter)
      .order("created_at", { ascending: false })
      .limit(200);

    if (error) {
      toast.error(error.message);
      setPackageOrders([]);
      return;
    }

    const rows = (data ?? []) as PackageOrderRow[];
    const userIds = [...new Set(rows.map((r) => r.user_id).filter(Boolean))];
    const { data: profs } = userIds.length
      ? await supabase.from("profiles").select("id,first_name,last_name,email").in("id", userIds)
      : { data: [] as { id: string; first_name: string; last_name: string; email: string }[] };
    const profMap = new Map((profs ?? []).map((p) => [p.id, p]));

    setPackageOrders(
      rows.map((r) => ({ ...r, profiles: profMap.get(r.user_id) ?? null })),
    );
  }, [filter]);

  const load = useCallback(async () => {
    setLoadingList(true);
    if (scope === "tickets") await loadTickets();
    else await loadPackages();
    setLoadingList(false);
  }, [scope, loadTickets, loadPackages]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  const filterTabs: { key: Filter; label: string; icon: typeof Clock }[] = [
    { key: "pending", label: "Bekleyen", icon: Clock },
    { key: "paid", label: "Ödendi", icon: CheckCircle2 },
  ];

  const scopeTabs: { key: Scope; label: string; icon: typeof Ticket }[] = [
    { key: "tickets", label: "Bilet Siparişleri", icon: Ticket },
    { key: "packages", label: "J Paketi Siparişleri", icon: Coins },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Ödemeler</h1>
          <p className="mt-2 text-muted-foreground">
            Shopier üzerinden ödeme yapan siparişleri burada manuel olarak onaylayın.
          </p>
        </div>

        <AdminTabs />

        <div className="mb-3 flex gap-2 rounded-xl border border-border bg-card p-1 shadow-card">
          {scopeTabs.map((t) => {
            const Icon = t.icon;
            const active = scope === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setScope(t.key)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mb-6 flex gap-2 rounded-xl border border-border bg-card p-1 shadow-card">
          {filterTabs.map((t) => {
            const Icon = t.icon;
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {loadingList ? (
          <div className="py-16 text-center text-muted-foreground">Yükleniyor...</div>
        ) : scope === "tickets" ? (
          orders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
              Bu kategoride sipariş yok.
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <OrderCard key={o.id} order={o} onChanged={load} />
              ))}
            </div>
          )
        ) : packageOrders.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
            Bu kategoride J paketi siparişi yok.
          </div>
        ) : (
          <div className="space-y-4">
            {packageOrders.map((o) => (
              <PackageOrderCard key={o.id} order={o} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order, onChanged }: { order: OrderRow; onChanged: () => void }) {
  const [notes, setNotes] = useState(order.admin_notes ?? "");
  const [submitting, setSubmitting] = useState(false);

  const markPaid = async () => {
    if (!confirm("Bu sipariş için Shopier'de ödeme alındığını onaylıyor musun? Biletler ve içecek kuponları otomatik oluşturulur.")) {
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.rpc("admin_mark_order_paid_shopier", {
      _order_id: order.id,
      _notes: notes.trim() || undefined,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Sipariş ödendi olarak işaretlendi, biletler oluşturuldu.");
    onChanged();
  };

  const cancelOrder = async () => {
    if (!confirm("Bu bekleyen siparişi iptal etmek istediğine emin misin? Kullanıcı yeni bilet alabilir hale gelir.")) {
      return;
    }
    setSubmitting(true);
    const { error } = await supabase
      .from("orders")
      .update({ status: "cancelled", admin_notes: notes.trim() || order.admin_notes })
      .eq("id", order.id);
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Sipariş iptal edildi.");
    onChanged();
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xs text-muted-foreground">#{order.id.slice(0, 8)}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(order.created_at).toLocaleString("tr-TR")}
            </span>
          </div>
          <h3 className="mt-1 text-lg font-bold">
            {(order.total_kurus / 100).toLocaleString("tr-TR")} ₺
          </h3>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {order.profiles && (
              <span className="inline-flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                {order.profiles.first_name} {order.profiles.last_name} · {order.profiles.email}
              </span>
            )}
            {order.events && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {order.events.name}
              </span>
            )}
          </div>
          <div className="mt-2 text-sm">
            {order.ticket_type_id && order.ticket_types && (
              <span className="mr-3">
                <span className="text-muted-foreground">Bilet:</span> {order.quantity}× {order.ticket_types.name}
              </span>
            )}
            <span className="text-muted-foreground">Yöntem:</span> {order.payment_method}
          </div>
          {order.admin_notes && (
            <div className="mt-2 rounded-lg bg-muted/50 p-2 text-xs">
              <span className="font-semibold">Not:</span> {order.admin_notes}
            </div>
          )}
        </div>
      </div>

      {order.status === "pending" && (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Not (opsiyonel) — ör. Shopier işlem no, tarih..."
            rows={2}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={markPaid} disabled={submitting} className="flex-1">
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckSquare className="mr-2 h-4 w-4" />
              )}
              Ödendi olarak işaretle & bilet oluştur
            </Button>
            <Button onClick={cancelOrder} disabled={submitting} variant="destructive" className="sm:w-auto">
              <X className="mr-2 h-4 w-4" />
              İptal et
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PackageOrderCard({ order, onChanged }: { order: PackageOrderRow; onChanged: () => void }) {
  const [notes, setNotes] = useState(order.admin_notes ?? "");
  const [submitting, setSubmitting] = useState(false);

  const markPaid = async () => {
    if (!confirm(`Bu J paketi siparişini ödendi olarak işaretle? Kullanıcının hesabına ${order.points} J₺ yüklenir.`)) {
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.rpc("admin_mark_package_order_paid", {
      _order_id: order.id,
      _notes: notes.trim() || undefined,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Sipariş ödendi olarak işaretlendi, ${order.points} J₺ yüklendi.`);
    onChanged();
  };

  const cancelOrder = async () => {
    if (!confirm("Bu bekleyen J paketi siparişini iptal etmek istediğine emin misin?")) {
      return;
    }
    setSubmitting(true);
    const { error } = await supabase
      .from("point_package_orders")
      .update({ status: "cancelled", admin_notes: notes.trim() || order.admin_notes })
      .eq("id", order.id);
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Sipariş iptal edildi.");
    onChanged();
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xs text-muted-foreground">#{order.id.slice(0, 8)}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(order.created_at).toLocaleString("tr-TR")}
            </span>
            {order.shopier_matched_at && (
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
                Shopier eşleşti
              </span>
            )}
          </div>
          <h3 className="mt-1 text-lg font-bold">
            {order.points} J₺ · {(order.price_kurus / 100).toLocaleString("tr-TR")} ₺
          </h3>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {order.profiles && (
              <span className="inline-flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                {order.profiles.first_name} {order.profiles.last_name} · {order.profiles.email}
              </span>
            )}
          </div>
          {(order.shopier_order_id || order.shopier_buyer_email) && (
            <div className="mt-2 text-xs text-muted-foreground">
              {order.shopier_order_id && <span className="mr-3">Shopier #: {order.shopier_order_id}</span>}
              {order.shopier_buyer_email && <span>Alıcı: {order.shopier_buyer_email}</span>}
            </div>
          )}
          {order.marked_paid_at && (
            <div className="mt-2 text-xs text-muted-foreground">
              Ödendi: {new Date(order.marked_paid_at).toLocaleString("tr-TR")}
            </div>
          )}
          {order.admin_notes && (
            <div className="mt-2 rounded-lg bg-muted/50 p-2 text-xs">
              <span className="font-semibold">Not:</span> {order.admin_notes}
            </div>
          )}
        </div>
      </div>

      {order.status === "pending" && (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Not (opsiyonel) — ör. Shopier işlem no, tarih..."
            rows={2}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={markPaid} disabled={submitting} className="flex-1">
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckSquare className="mr-2 h-4 w-4" />
              )}
              Ödendi olarak işaretle & J₺ yükle
            </Button>
            <Button onClick={cancelOrder} disabled={submitting} variant="destructive" className="sm:w-auto">
              <X className="mr-2 h-4 w-4" />
              İptal et
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
